import { Tag } from 'antd';
import { Routes } from 'config/routes';
import {
  DonationFailedReason,
  DonationOutput,
  PermissionsEnum,
} from 'gql/types';
import { getUniqueProductTypes } from 'modules/products/utils/getUniqueProductTypes';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import styles from './styles.module.scss';

type PropTypes = {
  donation: DonationOutput;
  productCategories?: Record<number, string>;
};

const OutcomeCell: FC<PropTypes> = ({ donation, productCategories }) => {
  const { t } = useTranslation('donation.DonationsTable.OutcomeCell');
  const currentUser = useCurrentUser();
  const isCurUserCanSeeProducts = currentUser.isCan(
    PermissionsEnum.ViewProductList
  );

  const isDiscarded = donation.products?.some(({ discardedProduct }) =>
    Boolean(discardedProduct)
  );

  if (donation.ineligible) {
    return (
      <Tag color="default">
        {t('ineligible')}
        {isDiscarded && ` (${t('discarded')})`}
      </Tag>
    );
  }

  if (donation.failedReason) {
    const failedReasonLabels: Record<DonationFailedReason, string> = {
      [DonationFailedReason.Medical]: t('medical'),
      [DonationFailedReason.Phlebotomy]: t('phlebotomy'),
      [DonationFailedReason.Restraint]: t('restraint'),
    };
    return (
      <Tag color="default">
        {t('failed')} ({failedReasonLabels[donation.failedReason]})
      </Tag>
    );
  }

  if (donation.products?.length) {
    const productNames = getUniqueProductTypes(donation)
      ?.map(
        (product) =>
          productCategories?.[product.woocommerceCategoryId] ||
          t('productDonation')
      )
      .join(' ');

    const productPath = isCurUserCanSeeProducts
      ? `${Routes.Products}?searchQuery="${donation.shortId}"`
      : '#';

    return (
      <Link
        className={!isCurUserCanSeeProducts ? styles.disabledLink : undefined}
        to={productPath}
      >
        {productNames}
        {isDiscarded && ` (${t('discarded')})`}
      </Link>
    );
  }

  return '-';
};

export default OutcomeCell;
